<template>
  <!-- Тестовая страница мероприятия -->
  <div class="container-page">
    <div class="activity-schedule">
      <!-- title -->
      <div class="activity-schedule__title-wrapper">
        <h4 class="activity-schedule__title">График мероприятий МПК</h4>
        <div class="activity-schedule__btn-create">
          <ButtonComponent mod="gradient-bg" @click="addCard()" v-if="canEdit">Создать мероприятие</ButtonComponent>
        </div>
      </div>

      <!-- filter -->
      <div class="activity-schedule__filter">
        <div class="activity-schedule__filter-block">
          <p class="activity-schedule__filter-header">Страна</p>
          <Multiselect
            placeholder="Выберите страну"
            label="text"
            searchable
            v-model="topPanelCountryId"
            valueProp="id"
            trackBy="text"
            :options="computedCountryList"
            noResultsText="Ничего не найдено."
          />
        </div>
        <div class="activity-schedule__filter-block activity-schedule__filter-block--buttons">
          <ButtonComponent mod="gradient-bg" @click="apply()">Показать</ButtonComponent>
          <ButtonComponent mod="border" @click="clear()">Сбросить</ButtonComponent>
        </div>
      </div>

      <!-- schedule -->
      <div class="activity-schedule__calendar">
        <Calendar
          :columns="getColumnCalendar"
          :step="1"
          :attributes="attributes"
          @loadCalendar="loadCalendar"
          @editCard="editCard"
        />
      </div>

      <!-- table -->
      <div
        class="activity-schedule-table"
        :class="{ 'activity-schedule-table--short-content': isLoading || !page.items.length }"
      >
        <PerfectScrollbar>
          <div class="activity-schedule__table table-container-new">
            <table class="stockTable">
              <thead>
                <tr>
                  <th class="th-icons" v-show="canEdit" scope="col"></th>
                  <th class="th-name" scope="col"><p>Название</p></th>
                  <th class="th-other th-other--sortable" scope="col">
                    <SortControl
                      class="activity-schedule__sort-th"
                      v-bind="dateSortControlProps"
                      @click="toggleSortByDate"
                    >
                      <p>Дата и время</p>
                    </SortControl>
                  </th>
                  <th class="th-other" scope="col"><p>Страна</p></th>
                  <th class="th-other" scope="col"><p>Место</p></th>
                  <th class="th-other" scope="col"><p>Тип мероприятия</p></th>
                  <th class="th-other" scope="col"><p>Регистрация</p></th>
                </tr>
                <tr class="activity-schedule__filters-row">
                  <th v-show="canEdit" scope="col"></th>
                  <th id="activity-schedule-th-filter-name" class="filter" scope="col">
                    <FilterInput
                      modClass="small"
                      clearButtonModClass="small"
                      placeholder="Введите название"
                      v-model="filters.name"
                      @valueChangedDebounced="onTextColumnFiltersChangeDebounced"
                    />
                  </th>
                  <th id="activity-schedule-th-filter-dates" class="filter" scope="col">
                    <div class="activity-schedule__date-range">
                      <FilterInput
                        modClass="filter-small"
                        type="date"
                        placeholder="от"
                        v-model="filters.dateFrom"
                        @valueChangedDebounced="onColumnFiltersChange"
                      />
                      <FilterInput
                        modClass="filter-small"
                        type="date"
                        placeholder="до"
                        v-model="filters.dateTo"
                        @valueChangedDebounced="onColumnFiltersChange"
                      />
                    </div>
                  </th>
                  <th id="activity-schedule-th-filter-country" class="filter" scope="col">
                    <Multiselect
                      class="activity-schedule__filter-multiselect"
                      placeholder="Все"
                      label="text"
                      searchable
                      v-model="filters.countryId"
                      valueProp="id"
                      trackBy="text"
                      :options="computedCountryList"
                      noResultsText="Ничего не найдено."
                      @update:modelValue="onTableCountryFilterChange"
                    />
                  </th>
                  <th id="activity-schedule-th-filter-place" class="filter" scope="col">
                    <FilterInput
                      modClass="small"
                      clearButtonModClass="small"
                      placeholder="Введите место"
                      v-model="filters.place"
                      @valueChangedDebounced="onTextColumnFiltersChangeDebounced"
                    />
                  </th>
                  <th scope="col" id="activity-schedule-th-filter-event-type" class="filter">
                    <Multiselect
                      class="activity-schedule__filter-multiselect"
                      placeholder="Все"
                      label="text"
                      searchable
                      v-model="filters.eventTypeId"
                      valueProp="id"
                      trackBy="text"
                      :options="eventTypeList"
                      noResultsText="Ничего не найдено."
                      @update:modelValue="onColumnFiltersChange"
                    />
                  </th>
                  <th scope="col" id="activity-schedule-th-filter-registration" class="filter">
                    <Multiselect
                      class="activity-schedule__filter-multiselect"
                      placeholder="Все"
                      label="text"
                      v-model="filters.registrationOption"
                      valueProp="id"
                      trackBy="text"
                      :options="registrationFilterOptions"
                      @update:modelValue="onColumnFiltersChange"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading" class="activity-schedule__empty-row">
                  <td v-show="canEdit" class="td-icons first-column-fixed"></td>
                  <td
                    class="activity-schedule__empty-cell activity-schedule__empty-cell--loader"
                    :colspan="activityScheduleTableDataColumnCount"
                  >
                    <Loader class="loader" />
                  </td>
                </tr>
                <template v-else-if="page.items.length">
                  <tr v-for="item in page.items" :key="item.id" @dblclick="editCard(item.id)">
                    <td v-show="canEdit" class="td-icons first-column-fixed">
                      <div>
                        <EventSvgTableEditIcon :itemId="item.id" :readonly="!canEdit" @edit="editCard" />
                        <EventSvgTableRemoveIcon :itemId="item.id" :readonly="!canEdit" @delete="deleteCard" />
                      </div>
                    </td>
                    <td>{{ item.name }}</td>
                    <td>
                      <template v-for="(date, index) in item.eventDatesList" :key="date">
                        <div>
                          {{ $momentFormat(date, Constants.DATE_FORMATS['D MMMM YYYY HH:mm'])
                          }}{{ index != item.eventDatesList.length - 1 ? ', ' : '' }}
                        </div>
                      </template>
                    </td>
                    <td>{{ getEventCountryName(item) }}</td>
                    <td>{{ item.place }}</td>
                    <td>{{ item.eventType.name }}</td>
                    <td>
                      <p
                        :class="{
                          green: item.isRegistrationOpen,
                          red: !item.isRegistrationOpen,
                        }"
                      >
                        {{ item.isRegistrationOpen ? 'Открыта' : 'Закрыта' }}
                      </p>
                    </td>
                  </tr>
                </template>
                <tr v-else class="activity-schedule__empty-row">
                  <td v-show="canEdit" class="td-icons first-column-fixed"></td>
                  <td class="activity-schedule__empty-cell" :colspan="activityScheduleTableDataColumnCount">
                    Нет данных
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </PerfectScrollbar>
        <div v-if="page.pagesTotal > 1 && page.items.length">
          <PaginationRedesigned
            :pageCount="page.pagesTotal"
            :currentPage="page.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { markRaw } from 'vue';
  import { PerfectScrollbar } from 'vue3-perfect-scrollbar';
  import Multiselect from '@vueform/multiselect';
  import { debounce } from 'lodash';
  import moment from 'moment';

  import FilterInput from '@/common/components/FilterInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Calendar from '@/common/components/redesigned/Calendar.vue';
  import PaginationRedesigned from '@/common/components/redesigned/PaginationRedesigned.vue';
  import SortControl from '@/common/components/SortControl.vue';
  import Constants from '@/common/constants';
  import { REGEX_API_DATE_ISO } from '@/common/constants/regex/dateRegex';
  import removeEmptyValuesFromObject from '@/common/mixins/removeEmptyValuesFromObject';
  import resizeEventListener from '@/common/mixins/resizeEventListener';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import Loader from '@/components/Loader.vue';

  import API from '../api/event';
  import EventSvgTableEditIcon from '../components/EventSvgTableEditIcon.vue';
  import EventSvgTableRemoveIcon from '../components/EventSvgTableRemoveIcon.vue';

  const EVENTS_SAVED_FILTER_KEYS = [
    'countryId',
    'name',
    'dateFrom',
    'dateTo',
    'place',
    'eventTypeId',
    'registrationOption',
  ];

  /** Колонки данных без колонки действий: название, дата, страна, место, тип, регистрация */
  const ACTIVITY_SCHEDULE_TABLE_DATA_COLUMNS = 6;

  export default {
    name: 'Events',
    components: {
      ButtonComponent,
      FilterInput,
      SortControl,
      EventSvgTableEditIcon,
      EventSvgTableRemoveIcon,
      PaginationRedesigned,
      Multiselect,
      Calendar,
      PerfectScrollbar,
      Loader,
    },
    mixins: [security, resizeEventListener, removeEmptyValuesFromObject],
    data() {
      return {
        eventListForCalendar: [],

        countryList: [],
        eventTypeList: [],
        topPanelCountryId: null,

        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        filters: {
          countryId: null,
          sortAsc: null,
          name: '',
          dateFrom: '',
          dateTo: '',
          place: '',
          eventTypeId: null,
          registrationOption: null,
        },
        page: {
          items: [],
          pagesTotal: 0,
          pageNumber: 1,
        },
        isLoading: false,
        canEdit: false,
        month: null,
        year: null,
        calendarRequestId: 0,

        windowWidth: window.innerWidth,
        Constants: Constants,
        activityScheduleTableDataColumnCount: ACTIVITY_SCHEDULE_TABLE_DATA_COLUMNS,

        debouncedReloadCalendarOnResize: markRaw(
          debounce(() => {
            if (this.month != null && this.year != null) {
              this.loadCalendar({ month: this.month, year: this.year });
            }
          }, 250),
        ),
        onTextColumnFiltersChangeDebounced: markRaw(
          debounce(() => {
            this.onColumnFiltersChange();
          }, 1500),
        ),
      };
    },
    created() {
      this.checkPermission(Constants.Permissions.MpkEventsEdit).then((response) => {
        this.canEdit = response.data;
      });
      loadFilter(this.$options.name).then((response) => {
        this.applyFiltersFromSavedResponse(response);
        const countryId = this.$route?.query?.countryId;
        if (countryId) {
          this.filters.countryId = countryId;
        }
        this.topPanelCountryId = this.filters.countryId;
        if (this.month != null && this.year != null) {
          this.loadCalendar({ month: this.month, year: this.year });
        }
        this.loadPage(false, false);
      });

      Utils.loadSelectOptions('countryList', this.countryList);
      Utils.loadSelectOptions('mpkEventTypeList', this.eventTypeList, { mpkEventTypeCategoryId: 1 }, true);
    },
    beforeUnmount() {
      this.debouncedReloadCalendarOnResize?.cancel();
      this.onTextColumnFiltersChangeDebounced?.cancel();
    },
    methods: {
      resolveCountryId(filterValue) {
        if (filterValue == null || filterValue === '') {
          return null;
        }
        const rawCountryId = typeof filterValue === 'object' ? filterValue.id ?? null : filterValue;
        if (rawCountryId == null || rawCountryId === '' || Number(rawCountryId) === 0) {
          return null;
        }
        return Number(rawCountryId);
      },
      /**
       * @param {Record<string, unknown> | null | undefined} response
       */
      applyFiltersFromSavedResponse(response) {
        if (!response) {
          return;
        }
        const patch = {};
        for (const key of EVENTS_SAVED_FILTER_KEYS) {
          if (Object.prototype.hasOwnProperty.call(response, key)) {
            patch[key] = response[key];
          }
        }
        Object.assign(this.filters, this.objectFormatter(patch));
        this.filters.countryId = this.resolveCountryId(this.filters.countryId);
        if (Object.prototype.hasOwnProperty.call(response, 'sortAsc')) {
          this.filters.sortAsc = response.sortAsc;
        }
      },
      /**
       * @param { { month?: number, year?: number } | undefined } payload — из v-calendar (update:fromPage) или явный вызов
       */
      loadCalendar(payload) {
        const currentCalendarRequestId = ++this.calendarRequestId;
        const page = payload && typeof payload === 'object' ? payload : null;
        if (page && page.month != null && page.year != null) {
          this.month = page.month;
          this.year = page.year;
        }
        if (this.month == null || this.year == null) {
          return;
        }
        const monthCount = this.getColumnCalendar || 3;
        const rangeStart = moment({ year: this.year, month: this.month - 1, day: 1 }).startOf('day');
        const rangeEnd = rangeStart
          .clone()
          .add(monthCount - 1, 'month')
          .endOf('month');
        const params = {
          pageNumber: Constants.paramsFullListDefault.pageNumber,
          pageSize: Constants.paramsFullListDefault.pageSize,
          dateFrom: rangeStart.format('YYYY-MM-DD'),
          dateTo: rangeEnd.format('YYYY-MM-DD'),
        };
        const selectedCountryId = this.resolveCountryId(this.topPanelCountryId ?? this.filters.countryId);
        if (selectedCountryId != null) {
          params.countryId = selectedCountryId;
        }
        this.eventListForCalendar = [];
        API.search(params)
          .then((response) => {
            if (currentCalendarRequestId !== this.calendarRequestId) {
              return;
            }
            response.data.items.forEach((eventItem) => {
              const eventDatesList = eventItem.eventDatesList;
              if (!eventDatesList || !eventDatesList.length) {
                return;
              }
              eventDatesList.forEach((calendarDate) => {
                if (calendarDate == null || calendarDate === '') {
                  return;
                }
                this.eventListForCalendar.push({
                  ...eventItem,
                  eventDatesList: [calendarDate],
                  time: moment(calendarDate).format('HH:mm'),
                });
              });
            });
          })
          .catch(() => {
            if (currentCalendarRequestId !== this.calendarRequestId) {
              return;
            }
            this.eventListForCalendar = [];
          });
      },

      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      normalizeApiDateParam(value) {
        if (!value) {
          return '';
        }
        const trimmed = String(value).trim();
        if (!trimmed) {
          return '';
        }
        if (REGEX_API_DATE_ISO.test(trimmed)) {
          return trimmed;
        }
        const parsed = moment(trimmed, ['DD.MM.YYYY', 'YYYY-MM-DD'], true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
      },
      buildSearchParams() {
        const f = this.filters;
        const countryId = this.resolveCountryId(f.countryId) ?? undefined;
        const eventTypeId = f.eventTypeId && +f.eventTypeId !== 0 ? +f.eventTypeId : undefined;
        const name = String(f.name ?? '').trim();
        const place = String(f.place ?? '').trim();
        const dateFrom = this.normalizeApiDateParam(f.dateFrom);
        const dateTo = this.normalizeApiDateParam(f.dateTo);
        const sortAsc = typeof f.sortAsc === 'boolean' ? f.sortAsc : undefined;
        const registrationOption =
          f.registrationOption === 1 || f.registrationOption === 2 ? f.registrationOption : undefined;

        return {
          ...(countryId !== undefined ? { countryId } : {}),
          ...(sortAsc !== undefined ? { sortAsc } : {}),
          ...(name ? { name } : {}),
          ...(place ? { place } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(eventTypeId !== undefined ? { eventTypeId } : {}),
          ...(registrationOption !== undefined ? { registrationOption } : {}),
        };
      },
      syncRouteQueryCountry() {
        const selectedCountryId = this.resolveCountryId(this.filters.countryId);
        const currentCountryId = this.resolveCountryId(this.$route?.query?.countryId);
        if (selectedCountryId === currentCountryId) {
          return;
        }

        const currentScrollY = window.scrollY;
        const currentQuery = this.$route?.query || {};
        const nextQuery = { ...currentQuery };

        if (selectedCountryId == null) {
          delete nextQuery.countryId;
        } else {
          nextQuery.countryId = selectedCountryId;
        }

        this.$router
          .replace({
            query: nextQuery,
          })
          .finally(() => {
            this.$nextTick(() => {
              window.scrollTo(0, currentScrollY);
            });
          });
      },
      onColumnFiltersChange() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filters);
        this.loadPage(false, false);
      },
      onTableCountryFilterChange() {
        const selectedCountryId = this.resolveCountryId(this.filters.countryId);
        this.filters.countryId = selectedCountryId;
        this.topPanelCountryId = selectedCountryId;
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filters);
        this.syncRouteQueryCountry();
        this.loadPage(false, false);
        this.loadCalendar({ month: this.month, year: this.year });
      },
      loadPage(addToExist, useCache) {
        const params = {
          ...this.buildSearchParams(),
          pageNumber: this.request.pageNumber,
          pageSize: this.request.pageSize,
        };

        this.isLoading = true;
        API.search(params, useCache)
          .then((response) => {
            if (!addToExist) {
              this.page = response.data || {
                items: [],
                pagesTotal: 0,
                pageNumber: 1,
              };
            } else if (response.data) {
              //добавляем к существующему
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },

      apply() {
        this.filters.countryId = this.resolveCountryId(this.topPanelCountryId);
        this.topPanelCountryId = this.filters.countryId;
        saveFilter(this.$options.name, this.filters);
        this.request.pageNumber = 1;
        this.loadPage(false, false);
        this.syncRouteQueryCountry();
        this.loadCalendar({ month: this.month, year: this.year });
      },
      clear() {
        this.topPanelCountryId = null;
        this.filters.countryId = null;
        this.filters.sortAsc = null;
        this.filters.name = '';
        this.filters.dateFrom = '';
        this.filters.dateTo = '';
        this.filters.place = '';
        this.filters.eventTypeId = null;
        this.filters.registrationOption = null;
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filters);
        this.loadPage(false, false);
        this.syncRouteQueryCountry();
        this.loadCalendar({ month: this.month, year: this.year });
      },

      toggleSortByDate() {
        switch (this.filters.sortAsc) {
          case null:
          case undefined:
            this.filters.sortAsc = true;
            break;
          case true:
            this.filters.sortAsc = false;
            break;
          default:
            this.filters.sortAsc = null;
        }
        saveFilter(this.$options.name, this.filters);
        this.request.pageNumber = 1;
        this.loadPage(false, false);
      },

      addCard() {
        this.$router.push({ name: 'EventCardAdd' });
      },
      editCard(id) {
        this.$router.push({
          name: 'EventCard',
          params: { id: id, action: this.canEdit ? 'edit' : 'view' },
        });
      },
      deleteCard(id) {
        Constants.alert.fire(Constants.confirmOptions.delete).then((res) => {
          if (res.isConfirmed) {
            API.delete(id)
              .then(() => {
                Constants.alert.fire('Удаление', 'Запись удалена', 'success');
                this.loadPage();
              })
              .catch((error) => {
                Constants.alert.fire(
                  'Удаление',
                  error.response && error.response.data ? error.response.data : 'Запись не удалена',
                  'error',
                );
              });
          }
        });
      },
      getEventCountryName(item) {
        if (!item) {
          return '';
        }
        const country = item.country;
        if (country) {
          if (typeof country.name === 'string' && country.name) {
            return country.name;
          }
          if (typeof country.text === 'string' && country.text) {
            return country.text;
          }
          if (typeof country.nameFull === 'string' && country.nameFull) {
            return country.nameFull;
          }
        }
        const countryId = item.countryId;
        if (countryId == null || countryId === '' || +countryId === 0) {
          return '';
        }
        const countryRow = this.countryList.find((countryOption) => +countryOption.id === +countryId);
        return countryRow?.text ?? '';
      },
      /**
       * @public
       * обрабатывается через миксин `resizeEventListener`
       */
      onResize() {
        this.windowWidth = window.innerWidth;
        this.debouncedReloadCalendarOnResize();
      },
    },
    computed: {
      registrationFilterOptions() {
        return [
          { id: 1, text: 'Открыта' },
          { id: 2, text: 'Закрыта' },
        ];
      },
      attributes() {
        return [
          // Attributes for todos
          ...this.eventListForCalendar.map((todo) => ({
            dates: todo.eventDatesList,
            dot: {
              color: todo.color,
              class: todo.statusId == 3 ? 'opacity-75' : '',
            },
            popover: {
              label: todo.name,
              visibility: 'click',
              isInteractive: true,
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
              ],
            },
            customData: todo,
          })),
        ];
      },
      // для правки отображения выбранного значения в `@vueform/multiselect`
      computedCountryList() {
        return [...this.countryList];
      },
      getColumnCalendar() {
        if (this.windowWidth >= 1280) return 3;
        if (this.windowWidth > 900) return 2;
        return 1;
      },
      // Проп order передаём только при активной сортировке (валидатор SortControl)
      dateSortControlProps() {
        switch (this.filters.sortAsc) {
          case true:
            return { order: 'ascending' };
          case false:
            return { order: 'descending' };
          default:
            return {};
        }
      },
    },
  };
</script>

<style lang="scss">
  .activity-schedule__filter-multiselect.multiselect,
  .activity-schedule__filter-block .multiselect {
    .multiselect-placeholder,
    .multiselect-single-label-text {
      font-weight: 400;
    }

    .multiselect-no-results {
      font-weight: 400;
    }
  }

  .activity-schedule-table {
    position: relative;
    z-index: 2;

    .activity-schedule__table {
      min-height: 210px;
    }

    .ps {
      max-height: 100% !important;
    }

    &--short-content {
      /* запас по высоте: календарь/селекты absolute — иначе перекрываются футером (он ниже в DOM) */
      padding-bottom: 220px;

      .ps {
        overflow: visible !important;
      }

      .stockTable thead th {
        overflow: visible;
      }
    }

    .v3dp__popout {
      z-index: 12000 !important;
    }

    .activity-schedule__filter-multiselect .multiselect-dropdown {
      z-index: 12000 !important;
    }
    .stockTable {
      table-layout: fixed;
      width: 100%;
    }

    .stockTable thead .th-name {
      width: 28%;
    }

    .stockTable thead .th-other--sortable {
      width: 16%;
    }

    .stockTable thead .th-other {
      width: 14%;
    }

    .stockTable thead tr:first-child th:last-child,
    .stockTable thead tr:first-child th:nth-last-child(2) {
      width: 13%;
    }

    .activity-schedule__filters-row th {
      padding: 12px 8px 12px 16px;
      border-bottom: 1px solid $light-grey-10;
      vertical-align: bottom;
      &.th-icons {
        padding-left: 0;
      }
    }
    .activity-schedule__date-range {
      display: flex;
      flex-direction: column;
      gap: 6px;
      justify-content: center;
    }
    .activity-schedule__filters-row {
      .input-group-wrapper,
      .clearable,
      .flex-container-label-left,
      .activity-schedule__filter-multiselect {
        width: 100%;
      }

      .input-group-wrapper,
      .clearable {
        align-items: center;
      }

      .input-group-wrapper.small,
      .input-group-wrapper.filter-small {
        margin-top: 0 !important;
      }

      .input-group-wrapper.small input:not([type='checkbox']):not([type='time']) {
        height: 40px;
        padding: 9px 14px;
        font-family: 'Fira Sans', sans-serif;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
      }

      .input-group-wrapper.small input::placeholder {
        font-family: 'Fira Sans', sans-serif;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
      }

      .date-component-filter-small .v3dp__input_wrapper input {
        height: 40px;
        padding: 9px 14px;
        font-family: 'Fira Sans', sans-serif !important;
        font-size: 14px !important;
        line-height: 20px !important;
        font-weight: 400 !important;
      }

      .date-component-filter-small .v3dp__input_wrapper input::placeholder {
        font-family: 'Fira Sans', sans-serif !important;
        font-size: 14px !important;
        line-height: 20px !important;
        font-weight: 400 !important;
      }

      .date-component-filter-small .v3dp__heading__center,
      .date-component-filter-small .v3dp__subheading,
      .date-component-filter-small .v3dp__elements button,
      .date-component-filter-small .v3dp__elements button span {
        font-weight: 400 !important;
      }

      .activity-schedule__filter-multiselect.multiselect {
        min-height: 40px;
      }

      .activity-schedule__filter-multiselect .multiselect-search,
      .activity-schedule__filter-multiselect .multiselect-placeholder,
      .activity-schedule__filter-multiselect .multiselect-single-label,
      .activity-schedule__filter-multiselect .multiselect-single-label-text {
        font-family: 'Fira Sans', sans-serif;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400 !important;
      }

      .activity-schedule__filter-multiselect .multiselect-option {
        font-family: 'Fira Sans', sans-serif;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400 !important;
      }

      #activity-schedule-th-filter-name .input-group-wrapper.small input,
      #activity-schedule-th-filter-place .input-group-wrapper.small input {
        font-family: 'Fira Sans', sans-serif !important;
        font-size: 14px !important;
        line-height: 20px !important;
        font-weight: 400 !important;
      }

      #activity-schedule-th-filter-name .input-group-wrapper.small input::placeholder,
      #activity-schedule-th-filter-place .input-group-wrapper.small input::placeholder {
        font-family: 'Fira Sans', sans-serif !important;
        font-size: 14px !important;
        line-height: 20px !important;
        font-weight: 400 !important;
      }
    }
    .activity-schedule__empty-cell {
      text-align: center;
      padding: 24px 16px;
      color: $light-grey-5;
    }

    .activity-schedule__empty-cell--loader {
      min-height: 110px;
    }

    .activity-schedule__empty-row .td-icons {
      border-bottom: 1px solid $light-grey-10;
    }

    .activity-schedule__filter-multiselect.multiselect {
      border-radius: 4px;
    }
    .activity-schedule__filter-multiselect .multiselect-dropdown {
      border: 1px solid $light-grey-5;
      box-shadow: 0px 0px 16px 0px #0000000d;
      border-radius: 4px;
    }
  }
  .activity-schedule__filter-block {
    .multiselect-dropdown {
      border: 1px solid $light-grey-5;
      box-shadow: 0px 0px 16px 0px #0000000d;
      border-radius: 4px;
      z-index: 9999;
    }
    .multiselect-option.is-selected {
      background: $dark-grey-1;
    }
    .multiselect-clear-icon {
      display: none;
    }
    .multiselect {
      border-radius: 4px;
    }
    .multiselect-option.is-pointed:hover {
      background: $light-grey-5 !important;
    }
  }
</style>

<style lang="scss" scoped>
  @import '../styles/Events.scss';
</style>
